import {
  Badge,
  Button,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  SearchBox,
  ToggleButton,
} from '@fluentui/react-components'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { createRestClient, queryClient, RestSchemas } from '@/contexts/QueryProvider'
import { useFooterHeight } from '@/hooks'
import { ErrorComponent } from '@/ui/ErrorComponent'
import { Loading } from '@/ui/Loading'
import { getAccessTableColumns } from './getAccessTableColumns'

type AppAccessesPanelProps = {
  appId: number
}

export type ApplicationAccessItem = {
  accessId: number
  nodeType: string
  nodeId: string
  nodeDescription: string
}

type TAccessMap = Map<number, ApplicationAccessItem>
type TRoleMap = Map<number, ApplicationAccessRole>
type TRoleAccessMap = Map<number, Map<number, boolean>>

export type ApplicationAccessRole = {
  roleId: number
  roleName: string
  roleDescription: string
}

export type TNodeTypeFilter = {
  count: number
  nodeType: string
}

type MessagesApplicationsApplicationAccess = RestSchemas['Messages.Applications.ApplicationAccess']

export const AppAccessesPanel = (props: AppAccessesPanelProps) => {
  const { getApplicationsApiGetAccesses, postApplicationsApiSetAccesses } = createRestClient()

  const [search, setSearch] = useState<string>('')
  const footerHeight = useFooterHeight()
  const [checkedMap, setCheckedMap] = useState<TRoleAccessMap>(new Map<number, Map<number, boolean>>())
  const [activeNodeTypeFilters, setActiveNodeTypeFilters] = useState<string[]>([])

  const {
    mutateAsync: saveAccess,
    isPending: isSaving,
    // isError: isSavingError,
  } = postApplicationsApiSetAccesses.useMutation()

  const selector = useCallback((data: MessagesApplicationsApplicationAccess[]) => {
    const rolesMap: TRoleMap = new Map<number, ApplicationAccessRole>()
    const rolesAccessCountMap = new Map<number, number>()
    const accessMap: TAccessMap = new Map<number, ApplicationAccessItem>()
    const accessAccessCountMap = new Map<number, number>()
    const newCheckedMap: Map<number, Map<number, boolean>> = new Map<number, Map<number, boolean>>()
    const nodeTypeFilters = new Map<string, number>()

    data.forEach((x) => {
      // cols
      if (x.roleId !== undefined && x.roleId !== null && !rolesMap.has(x.roleId!))
        rolesMap.set(x.roleId!, {
          roleId: x.roleId,
          roleName: x.roleName!,
          roleDescription: x.roleDescription!,
        })

      // checks
      if (x.roleId !== undefined && x.roleId !== null && x.id !== undefined) {
        const checkedAccessMap = newCheckedMap.get(x.roleId) ?? new Map<number, boolean>()
        checkedAccessMap.set(x.id!, x.accessGranted ?? false)
        newCheckedMap.set(x.roleId, checkedAccessMap)

        if (x.accessGranted == true) {
          const currentChecks = rolesAccessCountMap.get(x.roleId)
          rolesAccessCountMap.set(x.roleId, (currentChecks ?? 0) + 1)

          const currentChecksAccess = accessAccessCountMap.get(x.id)
          accessAccessCountMap.set(x.id, (currentChecksAccess ?? 0) + 1)
        }
      }

      // rows
      if (
        x.id !== undefined &&
        x.nodeId !== undefined &&
        x.nodeId !== null &&
        x.nodeType !== undefined &&
        x.nodeType !== null
      )
        if (!accessMap.has(x.id)) {
          if (!nodeTypeFilters.has(x.nodeType)) nodeTypeFilters.set(x.nodeType, 1)
          else nodeTypeFilters.set(x.nodeType, nodeTypeFilters.get(x.nodeType)! + 1)

          accessMap.set(x.id!, {
            accessId: x.id!,
            nodeDescription: x.description ?? '',
            nodeId: x.nodeId,
            nodeType: x.nodeType!,
          })
        }
    })

    setCheckedMap(newCheckedMap)

    const ntFilters = Array.from(nodeTypeFilters)
      .sort((a, b) => b[1] - a[1])
      .map((x) => {
        return {
          nodeType: x[0],
          count: x[1],
        } as TNodeTypeFilter
      })

    const applicationAccessList = Array.from(accessMap.values()).sort((a, b) => {
      const cmpCounts = (accessAccessCountMap.get(b.accessId) ?? 0) - (accessAccessCountMap.get(a.accessId) ?? 0)
      return cmpCounts == 0 ? b.nodeId.localeCompare(a.nodeId) : cmpCounts
    })

    const rolesArray = Array.from(rolesMap.values()).sort(
      (a, b) => (rolesAccessCountMap.get(b.roleId) ?? 0) - (rolesAccessCountMap.get(a.roleId) ?? 0)
    )

    const columns = getAccessTableColumns({ rolesArray, newCheckedMap, setCheckedMap })

    return {
      columns,
      applicationAccessList,
      nodeTypeFilters: ntFilters,
    }
  }, [])

  const { data, isPending, isError } = getApplicationsApiGetAccesses.useQuery(
    {
      query: { applicationId: props.appId },
    },
    {
      select: selector,
    }
  )

  const saveAccessSelections = () => {
    const proms: Promise<unknown>[] = []
    checkedMap.forEach((v, k) => {
      const accessesForRole = Array.from(v)
        .filter((x) => x[1] == true)
        .map((x) => x[0])

      const prom = saveAccess(
        {
          body: accessesForRole,
          query: {
            applicationId: props.appId,
            roleId: k,
          },
        },
        {
          onError: (_err) => {
            toast.error(`Error settings accesses for role ${k}`)
          },
        }
      )
      proms.push(prom)
    })

    Promise.all(proms)
      .then(() => {
        toast.success('Saved all roles')
      })
      .catch((_err) => {
        toast.error('Error settings accesses for roles')
      })
      .finally(() => {
        getApplicationsApiGetAccesses.invalidateQueries(
          { parameters: { query: { applicationId: props.appId } } },
          queryClient
        )
      })
  }

  const getRowId = useCallback((item: ApplicationAccessItem) => {
    return item.accessId
  }, [])

  if (isPending) return <Loading />
  if (isError) return <ErrorComponent />

  const { columns, applicationAccessList, nodeTypeFilters } = data

  const filteredItems = applicationAccessList.filter(
    (item) =>
      (activeNodeTypeFilters.length === 0 || activeNodeTypeFilters.includes(item.nodeType)) &&
      item.nodeId.includes(search.toLowerCase())
  )

  return (
    <div className="container-roles">
      <div>
        <div className="mb-5 flex flex-row flex-wrap justify-end gap-2">
          {nodeTypeFilters.map((n) => {
            return (
              <ToggleButton
                key={n.nodeType}
                size="small"
                shape="circular"
                defaultChecked={false}
                onClick={(e) => {
                  const current = [...activeNodeTypeFilters]
                  if (current.includes(n.nodeType)) {
                    const newFilters = current.filter((x) => x !== n.nodeType)
                    setActiveNodeTypeFilters(newFilters)
                  } else {
                    current.push(n.nodeType)
                    setActiveNodeTypeFilters(current)
                  }
                }}
              >
                {n.nodeType}: {n.count}
              </ToggleButton>
            )
          })}
          <SearchBox value={search} onChange={(e, data) => setSearch(data.value)} />
        </div>

        <div className="flex flex-row items-center justify-between bg-white p-5">
          <div>
            <Badge appearance="outline">{filteredItems.length} items</Badge>
          </div>
          {
            // <Button disabled={saving} onClick={saveAccessSelections}>
            <Button disabled={isSaving} onClick={saveAccessSelections}>
              Save
            </Button>
          }
        </div>

        <DataGrid
          items={filteredItems}
          columns={columns}
          sortable
          getRowId={getRowId}
          style={{ backgroundColor: 'white' }}
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell className="font-bold">{renderHeaderCell()}</DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<ApplicationAccessItem>
            style={{
              maxHeight: `calc(100vh - ${footerHeight}px - 250px - ${180}px)`,
              overflowY: 'auto',
            }}
          >
            {({ item, rowId }) => (
              <DataGridRow<ApplicationAccessItem> key={rowId}>
                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>
    </div>
  )
}
